import { getApp, getApps, initializeApp } from "firebase/app";
import {
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  type Auth,
  type User,
} from "firebase/auth";
import { readMindsetWebRuntimeConfig } from "./webRuntimeConfig";

export interface FirebaseWebCredential {
  idToken: string;
  userId: string;
}

export interface FirebaseWebIdentity {
  signInWithCustomToken?(token: string): Promise<FirebaseWebCredential>;
  resetPassword(email: string): Promise<void>;
  signIn(email: string, password: string): Promise<FirebaseWebCredential>;
  register(email: string, password: string): Promise<FirebaseWebCredential>;
  currentCredential(forceRefresh: boolean): Promise<FirebaseWebCredential | null>;
  signOut(): Promise<void>;
}

export class FirebaseWebIdentityError extends Error {
  constructor(message: string, readonly code: string | null = null) {
    super(message);
    this.name = "FirebaseWebIdentityError";
  }
}

export function createFirebaseWebIdentity(): FirebaseWebIdentity {
  let configuredAuth: Promise<Auth> | null = null;

  const auth = () => {
    configuredAuth ??= configureAuth();
    return configuredAuth;
  };

  return {
    async signInWithCustomToken(token) {
      try { return userCredential((await signInWithCustomToken(await auth(), token)).user, true); }
      catch (error) { throw friendlyIdentityError(error, "Mindset could not complete secure sign-in."); }
    },
    async resetPassword(email) {
      try {
        await sendPasswordResetEmail(await auth(), normalizeEmail(email));
      } catch (error) {
        const code = readErrorCode(error);
        // Account-specific responses must not reveal whether an identity exists.
        if (code === "auth/user-not-found" || code === "auth/user-disabled") return;
        throw new FirebaseWebIdentityError(
          code === "auth/invalid-email" ? "Enter a valid email address."
            : "Unable to request a reset right now. Wait a moment and try again.",
        );
      }
    },
    async signIn(email, password) {
      try {
        const credential = await signInWithEmailAndPassword(
          await auth(),
          normalizeEmail(email),
          password,
        );
        return userCredential(credential.user, true);
      } catch (error) {
        throw friendlyIdentityError(error, "Mindset could not sign you in.");
      }
    },
    async register(email, password) {
      try {
        const credential = await createUserWithEmailAndPassword(
          await auth(),
          normalizeEmail(email),
          password,
        );
        return userCredential(credential.user, true);
      } catch (error) {
        throw friendlyIdentityError(error, "Mindset could not create your sign-in.");
      }
    },
    async currentCredential(forceRefresh) {
      try {
        const instance = await auth();
        await instance.authStateReady();
        return instance.currentUser
          ? userCredential(instance.currentUser, forceRefresh)
          : null;
      } catch (error) {
        throw friendlyIdentityError(error, "Mindset could not refresh your sign-in.");
      }
    },
    async signOut() {
      try {
        await signOut(await auth());
      } catch (error) {
        throw friendlyIdentityError(error, "Mindset could not finish signing out.");
      }
    },
  };
}

async function configureAuth(): Promise<Auth> {
  const config = readMindsetWebRuntimeConfig();
  const app = getApps().length
    ? getApp()
    : initializeApp({
        apiKey: config.firebase.apiKey,
        appId: config.firebase.appId,
        authDomain: config.firebase.authDomain,
        projectId: config.firebase.projectId,
      });
  const auth = getAuth(app);
  await setPersistence(auth, browserSessionPersistence);
  return auth;
}

async function userCredential(
  user: User,
  forceRefresh: boolean,
): Promise<FirebaseWebCredential> {
  return {
    idToken: await user.getIdToken(forceRefresh),
    userId: user.uid,
  };
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function friendlyIdentityError(error: unknown, fallback: string): FirebaseWebIdentityError {
  const code = readErrorCode(error);
  const messages: Record<string, string> = {
    "auth/email-already-in-use": "That email already has a Mindset sign-in.",
    "auth/invalid-credential": "The email or password is incorrect.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/missing-password": "Enter your password.",
    "auth/network-request-failed": "Mindset could not reach secure sign-in. Check your connection and try again.",
    "auth/too-many-requests": "Too many sign-in attempts. Wait a moment and try again.",
    "auth/user-disabled": "This Mindset sign-in is disabled.",
    "auth/weak-password": "Use a password with at least six characters.",
  };
  return new FirebaseWebIdentityError(messages[code ?? ""] ?? fallback, code);
}

function readErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("code" in error)) return null;
  return typeof error.code === "string" ? error.code : null;
}
